import { Card, CardContent, Typography, Box, Stack } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Sparkles } from 'lucide-react';

interface SentimentData {
  positive: number;
  neutral: number;
  negative: number;
}

interface SentimentAnalysisChartProps {
  data: SentimentData;
  hasReviews?: boolean;
}

const SentimentAnalysisChart = ({ data, hasReviews = true }: SentimentAnalysisChartProps) => {
  const total = data.positive + data.neutral + data.negative;
  
  const chartData = [
    { name: 'Positive', value: data.positive, color: '#F1554C' },
    { name: 'Neutral', value: data.neutral, color: '#0F296D' },
    { name: 'Negative', value: data.negative, color: '#FFA5A0' }
  ];

  const getPercentage = (value: number) => {
    return total > 0 ? Math.round((value / total) * 100) : 0;
  };

  return (
    <Card
      sx={{
        background: '#FFFFFF',
        border: '1px solid #E4E4E7',
        borderRadius: '20px',
        boxShadow: '0px 5.54363px 35.1097px rgba(0, 0, 0, 0.04)',
        height: '367px',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {!hasReviews && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.75) 0%, rgba(249, 250, 251, 0.85) 100%)',
            backdropFilter: 'blur(2px)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0px 8px 24px rgba(79, 70, 229, 0.25)'
            }}
          >
            <Sparkles size={32} color="#FFFFFF" />
          </Box>
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '12px',
              p: 3,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(79, 70, 229, 0.1)',
              maxWidth: '320px'
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '24px',
                color: '#111827',
                mb: 1
              }}
            >
              Insights Coming Soon! 🎯
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6B7280'
              }}
            >
              Once your customers start posting reviews, we'll analyze the sentiment and show you beautiful insights here!
            </Typography>
          </Box>
        </Box>
      )}
      <CardContent sx={{ p: '24px', height: '100%' }}>
        {/* Title */}
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '20px',
            color: '#393D54',
            mb: 3
          }}
        >
          Sentiment Analysis
        </Typography>

        {/* Content Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 'calc(100% - 60px)',
            gap: 3
          }}
        >
          {/* Legend */}
          <Stack spacing={3} sx={{ flex: '0 0 auto' }}>
            {/* Positive */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                <Box
                  sx={{
                    width: '15.4px',
                    height: '15.4px',
                    background: '#F1554C',
                    borderRadius: '2.46px'
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: '15px',
                    lineHeight: '19px',
                    color: '#393D54'
                  }}
                >
                  Positive
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#0F296D',
                  ml: '23.4px'
                }}
              >
                {data.positive.toLocaleString()} Reviews ({getPercentage(data.positive)}%)
              </Typography>
            </Box>

            {/* Neutral */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                <Box
                  sx={{
                    width: '15.4px',
                    height: '15.4px',
                    background: '#0F296D',
                    borderRadius: '2.46px'
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '18px',
                    color: '#393D54'
                  }}
                >
                  Neutral
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '15px',
                  color: '#0F296D',
                  ml: '23.4px'
                }}
              >
                {data.neutral.toLocaleString()} Reviews ({getPercentage(data.neutral)}%)
              </Typography>
            </Box>

            {/* Negative */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                <Box
                  sx={{
                    width: '15.4px',
                    height: '15.4px',
                    background: '#FFA5A0',
                    borderRadius: '2.46px'
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 500,
                    fontSize: '15px',
                    lineHeight: '19px',
                    color: '#393D54'
                  }}
                >
                  Negative
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#0F296D',
                  ml: '23.4px'
                }}
              >
                {data.negative.toLocaleString()} Reviews ({getPercentage(data.negative)}%)
              </Typography>
            </Box>
          </Stack>

          {/* Donut Chart */}
          <Box sx={{ flex: '1 1 auto', height: '100%', minHeight: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="85%"
                  paddingAngle={0}
                  dataKey="value"
                  startAngle={90}
                  endAngle={450}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SentimentAnalysisChart;
